import { useCallback, useEffect, useRef, useState } from 'react'
import Header from '../../components/header/header'
import { useLocation, useNavigate, useParams } from 'react-router'
import {
    getSingleCampaignDetailAction,
    getProfileByIdsAction,
    reviewCampaignAction,
} from '../../actions/peopleai'
import { useDispatch, useSelector } from 'react-redux'
import ReactPaginate from 'react-paginate'
import { Controller, useForm, useWatch } from 'react-hook-form'
import Select from "react-select"
import {
    appUrl,
    backendUrl,
    CUSTOM_SELECT_STYLES,
    SELECT_BOX_THEME,
} from '../../constants'
import CopyToClipboard from 'react-copy-to-clipboard'
import toast from 'react-hot-toast'
import Loader from '../../components/loader'
import ViewProfileModal from '../../components/ViewProfileModal'
import CampaignConfirmation from './CampaignConfirmation'

const REQUEST_TO_CONNECT_STATUS = { label: "Request to connect", value: 'request_to_connect' }

export default function ViewCampaignDetail() {
    const { id } = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const filterParam = new URLSearchParams(location.search).get("filter")

    const { control, setValue } = useForm({
        defaultValues: {
            status: filterParam === "sendrequest" ? REQUEST_TO_CONNECT_STATUS : null,
        },
    })
    const status = useWatch({ control, name: "status" })
    const [open, setOpen] = useState(false);
    const [profileData, setProfileData] = useState(null);
    const [confirmAcceptRejectModal, setConfirmAcceptRejectModal] = useState(false);
    const [campaign, setCampaign] = useState(null);

    const [page, setPage] = useState(1)
    const [activePage, setActivePage] = useState(0)
    const [search, setSearch] = useState('')
    const [companyBannerImage, setCompanyBannerImage] = useState("/assets/img/peopleAi.png")

    /** 🔹 profileId → profileData */
    const [profileMap, setProfileMap] = useState({})
    /** 🔹 profileId → loading */
    const [profileLoadingMap, setProfileLoadingMap] = useState({})

    const {
        campaignProfileList,
        campaignProfileListCount,
        singleCampaignDetail,
        campaignProfileLoader,
    } = useSelector((state) => state.peopleai)

    const profileFilter = [
        REQUEST_TO_CONNECT_STATUS,
        { label: "Requested connection", value: 'requested_connetion' },
    ]

    /* -------------------------------------------
       Pagination
    -------------------------------------------- */
    const handlePageClick = (e) => {
        const selectedPage = e.selected
        setActivePage(selectedPage)
        setPage(selectedPage + 1)
    }

    /* -------------------------------------------
       Fetch campaign + profiles list
    -------------------------------------------- */
    const timeoutRef = useRef(null);
    const removeSendRequestFilter = useCallback(() => {
        if (filterParam !== "sendrequest") return

        const searchParams = new URLSearchParams(location.search)
        searchParams.delete("filter")

        navigate(
            {
                pathname: location.pathname,
                search: searchParams.toString(),
            },
            { replace: true }
        )
    }, [filterParam, location.pathname, location.search, navigate])

    useEffect(() => {
        setPage(1)
        setActivePage(0)
    }, [search, status])

    useEffect(() => {
        if (filterParam === "sendrequest") {
            setValue("status", REQUEST_TO_CONNECT_STATUS)
            removeSendRequestFilter()
        }
    }, [filterParam, removeSendRequestFilter, setValue])

    useEffect(() => {
        clearTimeout(timeoutRef.current); // always clear, no null check needed

        timeoutRef.current = setTimeout(() => {
            dispatch(getSingleCampaignDetailAction(id, page, 20, search, status?.value || ""))
        }, search ? 500 : 0); // debounce only when searching, instant when clearing

        return () => clearTimeout(timeoutRef.current);
    }, [dispatch, id, page, status, search])

    useEffect(() => {
        const bannerPath = singleCampaignDetail?.company?.banner

        if (!bannerPath) {
            setCompanyBannerImage("/assets/img/peopleAi.png")
            return
        }

        const bannerUrl = `${backendUrl}${bannerPath}`
        const img = new Image()

        img.onload = () => setCompanyBannerImage(bannerUrl)
        img.onerror = () => setCompanyBannerImage("/assets/img/peopleAi.png")
        img.src = bannerUrl
    }, [singleCampaignDetail?.company?.banner])

    /* -------------------------------------------
       Fetch profile by ID (cached)
    -------------------------------------------- */
    const fetchProfileById = async (profileId) => {
        if (!profileId) return null
        if (profileMap[profileId] || profileLoadingMap[profileId]) return
        try {
            setProfileLoadingMap(prev => ({ ...prev, [profileId]: true }))

            const resp = await dispatch(getProfileByIdsAction(profileId));
            const profileData = resp?.data?.data || resp?.data || null
            setProfileMap(prev => ({
                ...prev,
                [profileId]: profileData,
            }))
        } catch (e) {
            setProfileMap(prev => ({
                ...prev,
                [profileId]: null,
            }))
        } finally {
            setProfileLoadingMap(prev => ({
                ...prev,
                [profileId]: false,
            }))
        }
    }

    /* -------------------------------------------
       Auto-fetch missing profiles
    -------------------------------------------- */
    useEffect(() => {
        if (!campaignProfileList?.length) return

        const uniqueProfileIds = [
            ...new Set(
                campaignProfileList
                    .map(x => x?.profile)
                    .filter(Boolean)
            ),
        ]

        uniqueProfileIds.forEach((profileId) => {
            if (!profileMap[profileId] && !profileLoadingMap[profileId]) {
                fetchProfileById(profileId)
            }
        })
    }, [campaignProfileList])


    const handleViewProfile = (profile) => {
        setProfileData(profile);
        setOpen(true);
    };

    const onSubmitApproval = async (item) => {
        await dispatch(reviewCampaignAction(item?.id));
        dispatch(getSingleCampaignDetailAction(id, page, 20, search, status?.value || ""))
    };

    return (
        <>
            <Header title="Campaign manager" />
            <CampaignConfirmation
                show={confirmAcceptRejectModal}
                onClose={() => setConfirmAcceptRejectModal(false)}
                campaign={campaign}
                onSubmitApproval={onSubmitApproval}
                isDefault={true}
            />

            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="card planList notification h-100">
                            <div className="card-header">
                                <div className="searchbar">
                                    <input
                                        type="search"
                                        placeholder="Search profile by name.."
                                        name="search"
                                        id=""
                                        autoComplete="off"
                                        value={search}
                                        onChange={(e) => {
                                            setSearch(e.target.value.trimLeft());
                                        }}
                                    />
                                    <img src="assets/img/searchIcon.svg" alt="" />
                                </div>
                            </div>
                            {campaignProfileLoader ? (
                                <Loader />
                            ) : (
                                <>
                                    <div className="card-body detail p-0">

                                        {/* Header */}
                                        <div className='profileFilterHeader'>
                                            <h6 className='font-weight-bolder payment-text mb-0'>
                                                {singleCampaignDetail?.name}
                                            </h6>

                                            <div className='statusFilterProfile campaignDetailActions'>
                                                {singleCampaignDetail?.status === "inprogress" &&
                                                    <button
                                                        onClick={() => {
                                                            setCampaign(singleCampaignDetail);
                                                            setConfirmAcceptRejectModal(true);
                                                        }}
                                                        className="btn save-btn editButton"
                                                    >
                                                        Approve
                                                    </button>
                                                }
                                                <Controller
                                                    name="status"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Select
                                                            {...field}
                                                            onChange={(value) => {
                                                                field.onChange(value)
                                                                removeSendRequestFilter()
                                                            }}
                                                            theme={SELECT_BOX_THEME}
                                                            styles={CUSTOM_SELECT_STYLES}
                                                            className="select-border"
                                                            options={profileFilter}
                                                            isClearable
                                                            placeholder="Status"
                                                            components={{ IndicatorSeparator: () => null }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>

                                        {/* Banner + description */}
                                        <div className='headerNamePadding'>
                                            <div
                                                className='companybanner'
                                                style={{
                                                    backgroundImage: `url(${companyBannerImage})`
                                                }}
                                            />
                                            <p className='campaignDescription'>
                                                {singleCampaignDetail?.description}
                                            </p>
                                        </div>

                                        {/* Profiles */}
                                        <div className='accordion-card'>
                                            {campaignProfileList?.length > 0 ? (
                                                campaignProfileList.map((ele, i) => {
                                                    const profileId = ele?.profile
                                                    const profile = profileMap[profileId]
                                                    const isLoading = profileLoadingMap[profileId]

                                                    return (
                                                        <div key={i} className="accordionCustomHeader">
                                                            <div onClick={() => handleViewProfile(profile)} className='campaignaccordionBodyHolder'>

                                                                {/* Avatar */}
                                                                <div className='campaignImageHolder'>
                                                                    {isLoading ? (
                                                                        "Loading..."
                                                                        // <Loader small />
                                                                    ) : profile?.imageUrl ? (
                                                                        <img
                                                                            className='campaignprofileImage'
                                                                            src={profile.imageUrl}
                                                                            onError={(e) => {
                                                                                e.target.onerror = null
                                                                                e.target.src = "/assets/img/userChat.svg"
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <div className='campaignprofileImageText'>
                                                                            <p className="campaignprofileTitles">
                                                                                <span>
                                                                                    {profile?.name?.[0] ||
                                                                                        profile?.first_name?.[0] ||
                                                                                        "-"}
                                                                                </span>
                                                                            </p>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Details */}
                                                                <div className='campaignDescHolder'>
                                                                    <p className="campaignprofileTitles">
                                                                        {isLoading
                                                                            ? "Loading..."
                                                                            : <>
                                                                                {profile?.name || profile?.first_name || "-"}
                                                                                {profile?.title && (
                                                                                    <span> ({profile.title})</span>
                                                                                )}
                                                                            </>
                                                                        }
                                                                    </p>

                                                                    {profile?.location &&
                                                                        <p className="campaignprofileTitles">
                                                                            <span>{profile.location}</span>
                                                                        </p>
                                                                    }

                                                                    {profile?.about &&
                                                                        <p className="campaignprofileTitles">
                                                                            Description: <span>{profile.about}</span>
                                                                        </p>
                                                                    }

                                                                    {profile?.headline &&
                                                                        <p className="campaignprofileTitles">
                                                                            Headline: <span>{profile.headline}</span>
                                                                        </p>
                                                                    }

                                                                    {profile?.linkedinUrl &&
                                                                        <p className="campaignprofileTitles">
                                                                            Linkedin url: <span>{profile.linkedinUrl}</span>
                                                                        </p>
                                                                    }
                                                                    {profile?.sharedby && (
                                                                        <p className="campaignprofileTitles">
                                                                            Shared by {profile?.sharedby?.first_name} {profile?.sharedby?.last_name || ""}
                                                                        </p>
                                                                    )}
                                                                    {ele?.status === "request_to_connect" &&
                                                                        <p className="campaignprofileTitles">
                                                                            Status: <span className="publish campaignStatusChip">Request to connect</span>
                                                                        </p>
                                                                    }
                                                                </div>
                                                            </div>

                                                            {/* Copy link */}
                                                            <CopyToClipboard
                                                                text={`${appUrl}c/${singleCampaignDetail?.slug ? singleCampaignDetail?.slug : singleCampaignDetail?.id}/${ele?.slug ? ele?.slug : ele?.id}`}
                                                            >
                                                                <div
                                                                    className="copybtn"
                                                                    onClick={() => toast.success("Copied to clipboard!")}
                                                                >
                                                                    <button className="btn save-btn editButton">
                                                                        Copy link
                                                                    </button>
                                                                </div>
                                                            </CopyToClipboard>
                                                        </div>
                                                    )
                                                })
                                            ) : (
                                                <div className="p-3 text-center">
                                                    <p className="campaignprofileTitles">
                                                        No users found
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Pagination */}
                                    {campaignProfileListCount > 1 && (
                                        <div className="pagination-section">
                                            <ReactPaginate
                                                previousLabel={"Prev"}
                                                nextLabel={"Next"}
                                                breakLabel={"..."}
                                                pageCount={campaignProfileListCount}
                                                marginPagesDisplayed={1}
                                                pageRangeDisplayed={3}
                                                onPageChange={handlePageClick}
                                                containerClassName={"pagination"}
                                                activeClassName={"active"}
                                                forcePage={activePage}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <ViewProfileModal
                show={open}
                onClose={() => setOpen(false)}
                profile={profileData}
            />
        </>
    )
}
